import { Injectable } from '@angular/core';
import {Observable, of, shareReplay, Subject, switchMap} from "rxjs";
import {loadStripe} from "@stripe/stripe-js";
import {map, take, tap} from "rxjs/operators";
import {StoreSelectorsService} from "./store-selectors.service";
import {HttpClient} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {actionSelectedUserUpdate} from "../store/actions/user.actions";
import {IUser} from "../interfaces";
import _ from "lodash";
import {ToastrService} from "ngx-toastr";

@Injectable({
  providedIn: 'root'
})
export class FintechService {
    testKey = "pk_test_51PdWcODxKNLWTut5EIvnMa2VvDtG4hmBttOdzlhdIbcgBcBUJ0EBx31SADoK1qJmcyBLVQp6DOHPKx1JWvcBVv5K00yJwOOOFh";
    liveKey = "pk_live_51PdWcODxKNLWTut5fNR9i3anDzgt1zN3UjiBldVjHAhVHtP2xXmMWwSwLekhrzEADv3xL5wOfp4ialm7mpfYckh100H7KNYhUo";


    user: IUser;

    payerToken$;

    defaultFintech;
    businessId;
    payerId;
    resellerId;
    recurringMode;

    notify$: Subject<any>;
    busy$: Subject<any>;

    data;
    busy;

    paymentInfo;


    editMode = "info";

    stripe;

    fintechInfo$;


    constructor(private storeSelector: StoreSelectorsService,
                private http: HttpClient,
                private route: ActivatedRoute,
                private toastr: ToastrService) {
        this.notify$ = new Subject<any>();
        this.busy$ = new Subject<any>();
    }

    setBusy(busy) {
        this.busy = busy;
        this.busy$.next(busy);
    }

    getPayerToken(): Observable<any> {
        if (this.payerToken$ != null) {
            return this.payerToken$;
        }

        const payerToken = this.route.snapshot.paramMap.get('encrypted_id');
        if (payerToken != null) {
            this.payerToken$ = of(payerToken);
        } else {
            this.payerToken$ = of("token").pipe(
                switchMap( a => this.storeSelector.getUser()),
                take(1),
                switchMap(user => {
                    this.user = user;
                    const options = {
                        customerUser: user.email,
                        customerPass: user.passwordNew
                    };
                    const url = "https://galaxy.signage.me/Web/getPayerToken.ashx";
                    return this.http.post(url, options);
                }),
                map(reply => (<any> reply).token),
                shareReplay(1)
            );
        }
        return this.payerToken$;
    }


    load(i_data): Observable<any> {
        if (i_data.status == "fail") {
            console.error("error in server: " + i_data.error);
        }
        this.data = i_data;
        this.defaultFintech = i_data.defaultFintech;
        this.businessId = i_data.businessId;
        this.payerId = i_data.payerId;
        this.resellerId = i_data.resellerId;
        this.recurringMode = i_data.recurringMode;


        const publishedKey = i_data.sandbox ? this.testKey : this.liveKey;
        if (this.stripe != null) {
            return of(this.stripe);
        } else {
            return new Observable( sub => {
                loadStripe(publishedKey).then( stripe => {
                    this.stripe = stripe;
                    sub.next(stripe);
                    sub.complete();
                });
            });
        }
    }

    getFintechInfo(): Observable<any> {
        if (this.fintechInfo$ != null) {
            return this.fintechInfo$;
        }
        this.fintechInfo$ = this.getPayerToken().pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/getFintechInfo";
                const options = {token: payerToken};
                return this.http.post(url, options);
            }),
            switchMap(ret => this.load(ret)),
            tap(a => {
                this.notify$.next("update");
            })
        );
        return this.fintechInfo$;
    }


    selectPayMethod(i_recurringPayMethodId, i_orderPayMethodId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/selectPayMethod";
                const options = {
                    token: payerToken,
                    recurringPayMethodId: i_recurringPayMethodId,
                    orderPayMethodId: i_orderPayMethodId
                };
                return this.http.post(url, options);
            })
        );
    }

    deletePayMethod(i_payMethodId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/deletePayMethod";
                const options = { token: payerToken, payMethodId: i_payMethodId };
                return this.http.post(url, options);
            })
        );
    }

    updateAccountStatus(i_status) {
        if (this.user != null) {
            const usr = _.cloneDeep(this.user);
            usr.accountStatusReseller = i_status;
            this.storeSelector.dispatch(actionSelectedUserUpdate({user: usr}));
            this.toastr.clear();
        }
    }

    unsubscribe() {
        return this.getPayerToken().pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/unsubscribe";
                const options = {token: payerToken};
                return this.http.post(url, options);
            })
        );
    }

    getOrderInfo(i_orderId): Observable<any> {
        return this.getPayerToken().pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/getOrderInfo";
                const options = { token: payerToken, orderId: i_orderId };
                return this.http.post(url, options);
            })
        );
    }

    createOrderInfo(i_orderId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/createOrderInfo";
                const options = { token: payerToken, orderId: i_orderId };
                return this.http.post(url, options);
            })
        );
    }

    updateOrderInfo(orderInfo): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/fintech/updateOrderInfo";
                orderInfo.token = payerToken;
                return this.http.post(url, orderInfo);
            })
        );
    }
}
